/**
 * PandaDoc from the platform: send a proposal or engagement letter from a lead, track its status,
 * and download the signed copy. Server side only (/api/pandadoc and /api/cron/pandadoc-sync, src/server/api.ts);
 * the API key never reaches the browser.
 *
 * Works on PandaDoc's free plan, which includes the API for creating documents from a template,
 * sending them for signature, checking their status and downloading them (60 documents a year).
 * Webhooks need a paid plan, so status is checked on demand and every hour instead.
 *
 * Environment (the platform host's server settings, never VITE_ variables):
 *   PANDADOC_API_KEY                          production key, PandaDoc Dev Center (account owner or admin)
 *   SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY   where document records are kept
 *
 * The template in PandaDoc needs one recipient role called "Client" and may use these variables:
 *   [Client.Company] [Client.FirstName] [Client.LastName] [Client.Email] [Ryoma.OrdersBand]
 *   [Ryoma.AuditFee] [Ryoma.ImplementationFee] [Ryoma.Retainer] [Ryoma.ReportDays]
 *   [Ryoma.ImplementationWeeks] [Ryoma.Sender] [Ryoma.Date]
 */

export interface DocsEnv {
  PANDADOC_API_KEY?: string;
  SUPABASE_URL?: string;
  SUPABASE_SERVICE_ROLE_KEY?: string;
}

type Fetch = typeof fetch;
export const PANDADOC_API = "https://api.pandadoc.com/public/v1";
export const RECIPIENT_ROLE = "Client";
/** PandaDoc's free plan allows 60 documents a year. */
export const FREE_PLAN_DOCS_PER_YEAR = 60;

export type DocStatus = "draft" | "sent" | "viewed" | "completed" | "declined" | "voided" | "error";
export type DocKind = "proposal" | "engagement_letter" | "other";
export const FINAL_STATUSES: DocStatus[] = ["completed", "declined", "voided", "error"];
export const DOC_STATUS_LABEL: Record<DocStatus, string> = {
  draft: "Draft, not sent", sent: "Sent, not opened", viewed: "Opened, not signed", completed: "Signed",
  declined: "Declined", voided: "Withdrawn", error: "Error",
};
export const DOC_KIND_LABEL: Record<DocKind, string> = { proposal: "Proposal", engagement_letter: "Engagement letter", other: "Other" };

export interface LeadDocument {
  id?: string;
  lead_id: string;
  pandadoc_id: string;
  kind: DocKind;
  name: string;
  template_name: string | null;
  recipient_email: string | null;
  status: DocStatus;
  sent_at: string | null;
  viewed_at: string | null;
  completed_at: string | null;
  last_checked_at: string | null;
  created_by: string | null;
  created_at?: string;
}

/** PandaDoc's document statuses, mapped to ours. */
export function mapStatus(s: string | undefined | null): DocStatus {
  switch (s) {
    case "document.uploaded": case "document.draft": case "document.waiting_approval": case "document.approved": return "draft";
    case "document.sent": case "document.waiting_pay": case "document.external_review": return "sent";
    case "document.viewed": return "viewed";
    case "document.completed": case "document.paid": return "completed";
    case "document.declined": case "document.rejected": return "declined";
    case "document.voided": return "voided";
    default: return "error";
  }
}

export interface SendInput {
  lead_id: string;
  template_id: string;
  template_name?: string | null;
  kind: DocKind;
  recipient: { email: string; first_name: string; last_name: string };
  company: string;
  orders_band: string | null;
  audit_fee_pence: number | null;
  implementation_fee_pence: number | null;
  retainer_pence: number | null;
  sender: string;
  subject?: string;
  message?: string;
}

const money = (p: number | null) => (p === null ? "" : `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: p % 100 ? 2 : 0, maximumFractionDigits: 2 })}`);

/** The template variables we fill. Fees are ex VAT; empty where not agreed yet. */
export function tokensFor(i: SendInput, today = new Date()): { name: string; value: string }[] {
  return [
    { name: "Client.Company", value: i.company },
    { name: "Client.FirstName", value: i.recipient.first_name },
    { name: "Client.LastName", value: i.recipient.last_name },
    { name: "Client.Email", value: i.recipient.email },
    { name: "Ryoma.OrdersBand", value: i.orders_band ?? "" },
    { name: "Ryoma.AuditFee", value: money(i.audit_fee_pence) },
    { name: "Ryoma.ImplementationFee", value: money(i.implementation_fee_pence) },
    { name: "Ryoma.Retainer", value: i.retainer_pence === null ? "" : `${money(i.retainer_pence)} a month` },
    { name: "Ryoma.ReportDays", value: "5 working days" },
    { name: "Ryoma.ImplementationWeeks", value: "up to 6 weeks" },
    { name: "Ryoma.Sender", value: i.sender },
    { name: "Ryoma.Date", value: today.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/London" }) },
  ];
}

// ----------------------------------------------------------------------------- HTTP helpers

const json = (status: number, body: unknown) =>
  new Response(JSON.stringify(body), { status, headers: { "Content-Type": "application/json", "Cache-Control": "no-store" } });

async function panda(env: DocsEnv, f: Fetch, path: string, init: RequestInit = {}): Promise<Response> {
  if (!env.PANDADOC_API_KEY) throw new Error("PANDADOC_API_KEY is not set");
  return f(`${PANDADOC_API}${path}`, { ...init, headers: { Authorization: `API-Key ${env.PANDADOC_API_KEY}`, "Content-Type": "application/json", ...(init.headers ?? {}) } });
}

async function pandaJson<T>(env: DocsEnv, f: Fetch, path: string, init: RequestInit = {}): Promise<T> {
  const res = await panda(env, f, path, init);
  if (!res.ok) throw new Error(`PandaDoc ${res.status}: ${(await res.text()).slice(0, 200)}`);
  return (await res.json()) as T;
}

function rest(env: DocsEnv, f: Fetch, path: string, init: RequestInit = {}): Promise<Response> {
  if (!env.SUPABASE_URL || !env.SUPABASE_SERVICE_ROLE_KEY) throw new Error("SUPABASE_URL and SUPABASE_SERVICE_ROLE_KEY must be set");
  return f(`${env.SUPABASE_URL.replace(/\/$/, "")}${path}`, {
    ...init,
    headers: { apikey: env.SUPABASE_SERVICE_ROLE_KEY, Authorization: `Bearer ${env.SUPABASE_SERVICE_ROLE_KEY}`, "Content-Type": "application/json", ...(init.headers ?? {}) },
  });
}

async function restJson<T>(env: DocsEnv, f: Fetch, path: string, init: RequestInit = {}): Promise<T> {
  const res = await rest(env, f, path, init);
  if (!res.ok) throw new Error(`Supabase ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const t = await res.text();
  return (t ? JSON.parse(t) : null) as T;
}

/**
 * Who is calling: the signed-in founder's Supabase token, checked against the staff allow-list.
 * Returns their email, or null.
 */
export async function staffFromRequest(req: Request, env: DocsEnv, f: Fetch = fetch): Promise<string | null> {
  const token = req.headers.get("authorization")?.replace(/^Bearer\s+/i, "");
  if (!token || !env.SUPABASE_URL || !env.SUPABASE_SERVICE_ROLE_KEY) return null;
  const base = env.SUPABASE_URL.replace(/\/$/, "");
  const userRes = await f(`${base}/auth/v1/user`, { headers: { apikey: env.SUPABASE_SERVICE_ROLE_KEY, Authorization: `Bearer ${token}` } });
  if (!userRes.ok) return null;
  const email = String(((await userRes.json()) as { email?: string }).email ?? "").toLowerCase();
  if (!email.endsWith("@ryoma-insight.co.uk")) return null;
  const rows = await restJson<{ email: string }[]>(env, f, `/rest/v1/staff_allowlist?select=email&email=eq.${encodeURIComponent(email)}`);
  return rows?.length ? email : null;
}

// ----------------------------------------------------------------------------- actions

interface PandaDocument { id: string; name?: string; status?: string; date_sent?: string | null; date_completed?: string | null }

export async function listTemplates(env: DocsEnv, f: Fetch = fetch): Promise<{ id: string; name: string }[]> {
  const r = await pandaJson<{ results?: { id: string; name: string }[] }>(env, f, "/templates?count=100");
  return (r.results ?? []).map((t) => ({ id: t.id, name: t.name }));
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Creates the document from the template, waits for PandaDoc to finish preparing it, sends it,
 * records it and moves the lead to "Engagement letter sent".
 */
export async function createAndSend(input: SendInput, by: string, env: DocsEnv, f: Fetch = fetch, wait = sleep): Promise<LeadDocument> {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.recipient.email)) throw new Error("The recipient needs a valid email");
  const name = `${DOC_KIND_LABEL[input.kind]}: ${input.company}`;
  const created = await pandaJson<PandaDocument>(env, f, "/documents", {
    method: "POST",
    body: JSON.stringify({
      name,
      template_uuid: input.template_id,
      recipients: [{ ...input.recipient, role: RECIPIENT_ROLE }],
      tokens: tokensFor(input),
      metadata: { ryoma_lead_id: input.lead_id },
      tags: ["ryoma-platform"],
    }),
  });
  // PandaDoc prepares the document in the background; it can only be sent once it is a draft.
  let status = created.status ?? "document.uploaded";
  for (let i = 0; i < 10 && status === "document.uploaded"; i++) {
    await wait(1000);
    status = (await pandaJson<PandaDocument>(env, f, `/documents/${created.id}`)).status ?? status;
  }
  let sent = false;
  if (status === "document.draft") {
    await pandaJson(env, f, `/documents/${created.id}/send`, {
      method: "POST",
      body: JSON.stringify({ silent: false, subject: input.subject ?? name, message: input.message ?? "Please review and sign. Any questions, just reply to this email." }),
    });
    sent = true;
  }
  const now = new Date().toISOString();
  const doc: LeadDocument = {
    lead_id: input.lead_id, pandadoc_id: created.id, kind: input.kind, name, template_name: input.template_name ?? null,
    recipient_email: input.recipient.email.toLowerCase(), status: sent ? "sent" : mapStatus(status), sent_at: sent ? now : null,
    viewed_at: null, completed_at: null, last_checked_at: now, created_by: by,
  };
  const [row] = await restJson<LeadDocument[]>(env, f, "/rest/v1/lead_documents", { method: "POST", headers: { Prefer: "return=representation" }, body: JSON.stringify(doc) });
  if (sent) {
    await restJson(env, f, `/rest/v1/leads?id=eq.${input.lead_id}&status=in.(replied,call_booked,call_done)`, { method: "PATCH", body: JSON.stringify({ status: "proposal_sent", last_contact_at: now }) });
    await note(env, f, input.lead_id, by, `${DOC_KIND_LABEL[input.kind]} sent through PandaDoc to ${input.recipient.email}.`);
  }
  return row ?? doc;
}

async function note(env: DocsEnv, f: Fetch, leadId: string, by: string | null, body: string) {
  await restJson(env, f, "/rest/v1/lead_notes", { method: "POST", body: JSON.stringify({ lead_id: leadId, by_email: by, kind: "system", body }) });
}

/** Sends a document that was still being prepared when it was created. */
export async function sendDraft(doc: LeadDocument, by: string, env: DocsEnv, f: Fetch = fetch): Promise<LeadDocument> {
  await pandaJson(env, f, `/documents/${doc.pandadoc_id}/send`, { method: "POST", body: JSON.stringify({ silent: false, subject: doc.name, message: "Please review and sign. Any questions, just reply to this email." }) });
  const now = new Date().toISOString();
  const [row] = await restJson<LeadDocument[]>(env, f, `/rest/v1/lead_documents?id=eq.${doc.id}`, { method: "PATCH", headers: { Prefer: "return=representation" }, body: JSON.stringify({ status: "sent", sent_at: now, last_checked_at: now }) });
  await restJson(env, f, `/rest/v1/leads?id=eq.${doc.lead_id}&status=in.(replied,call_booked,call_done)`, { method: "PATCH", body: JSON.stringify({ status: "proposal_sent", last_contact_at: now }) });
  await note(env, f, doc.lead_id, by, `${DOC_KIND_LABEL[doc.kind]} sent through PandaDoc to ${doc.recipient_email ?? "the client"}.`);
  return row ?? { ...doc, status: "sent", sent_at: now };
}

/** Asks PandaDoc for the current status and records any change, with a note on the lead. */
export async function refreshDocument(doc: LeadDocument, env: DocsEnv, f: Fetch = fetch): Promise<LeadDocument> {
  const p = await pandaJson<PandaDocument>(env, f, `/documents/${doc.pandadoc_id}`);
  const status = mapStatus(p.status);
  const now = new Date().toISOString();
  const patch: Partial<LeadDocument> = { status, last_checked_at: now };
  if (status === "viewed" && !doc.viewed_at) patch.viewed_at = now;
  if (status === "completed" && !doc.completed_at) patch.completed_at = p.date_completed ?? now;
  const [row] = await restJson<LeadDocument[]>(env, f, `/rest/v1/lead_documents?id=eq.${doc.id}`, { method: "PATCH", headers: { Prefer: "return=representation" }, body: JSON.stringify(patch) });
  if (status !== doc.status) {
    if (status === "completed") {
      await note(env, f, doc.lead_id, null, `${DOC_KIND_LABEL[doc.kind]} signed in PandaDoc. Next: start the audit.`);
      await restJson(env, f, `/rest/v1/leads?id=eq.${doc.lead_id}&status=eq.proposal_sent`, { method: "PATCH", body: JSON.stringify({ next_step: "Signed: start the audit", next_step_due: now.slice(0, 10), last_contact_at: now }) });
    } else if (status === "declined") {
      await note(env, f, doc.lead_id, null, `${DOC_KIND_LABEL[doc.kind]} declined in PandaDoc.`);
    } else if (status === "viewed") {
      await note(env, f, doc.lead_id, null, `${DOC_KIND_LABEL[doc.kind]} opened by the client.`);
    }
  }
  return row ?? { ...doc, ...patch };
}

/** Every hour: checks documents that are still waiting for the client. */
export async function syncDocuments(env: DocsEnv, f: Fetch = fetch): Promise<{ checked: number; changed: number }> {
  if (!env.PANDADOC_API_KEY || !env.SUPABASE_URL || !env.SUPABASE_SERVICE_ROLE_KEY) return { checked: 0, changed: 0 };
  const open = await restJson<LeadDocument[]>(env, f, "/rest/v1/lead_documents?select=*&status=in.(draft,sent,viewed)&order=last_checked_at.asc.nullsfirst&limit=50");
  let changed = 0;
  for (const d of open ?? []) {
    const after = await refreshDocument(d, env, f);
    if (after.status !== d.status) changed++;
  }
  return { checked: open?.length ?? 0, changed };
}

/** The function's single entry point: { action: "templates" | "send" | "send-draft" | "refresh" | "download", ... } */
export async function handleDocuments(req: Request, env: DocsEnv, f: Fetch = fetch, wait = sleep): Promise<Response> {
  if (req.method !== "POST") return json(405, { error: "POST only" });
  if (!env.PANDADOC_API_KEY) return json(503, { error: "PandaDoc is not connected yet: set PANDADOC_API_KEY in the platform's server settings" });
  const by = await staffFromRequest(req, env, f);
  if (!by) return json(401, { error: "Sign in with a Ryoma account on the staff list" });
  let body: Record<string, unknown>;
  try { body = (await req.json()) as Record<string, unknown>; } catch { return json(400, { error: "Bad request" }); }
  try {
    const docById = async (id: unknown) => {
      if (typeof id !== "string" || !/^[0-9a-f-]{36}$/i.test(id)) throw new Error("Unknown document");
      const [d] = await restJson<LeadDocument[]>(env, f, `/rest/v1/lead_documents?select=*&id=eq.${id}`);
      if (!d) throw new Error("Unknown document");
      return d;
    };
    switch (body.action) {
      case "templates": return json(200, { templates: await listTemplates(env, f) });
      case "send": return json(200, { document: await createAndSend(body.input as SendInput, by, env, f, wait) });
      case "send-draft": return json(200, { document: await sendDraft(await docById(body.id), by, env, f) });
      case "refresh": return json(200, { document: await refreshDocument(await docById(body.id), env, f) });
      case "download": {
        const d = await docById(body.id);
        const res = await panda(env, f, `/documents/${d.pandadoc_id}/download`);
        if (!res.ok) throw new Error(`PandaDoc ${res.status}: the document is not ready to download`);
        return new Response(res.body, { status: 200, headers: { "Content-Type": "application/pdf", "Content-Disposition": `attachment; filename="${d.name.replace(/[^\w .:-]/g, "")}.pdf"`, "Cache-Control": "no-store" } });
      }
      default: return json(400, { error: "Unknown action" });
    }
  } catch (e) {
    return json(502, { error: e instanceof Error ? e.message : String(e) });
  }
}
